import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

export const FILTER_PARAM = 'filter'
export const FILTERS_TOUCHED_STATE_KEY = 'advancedFiltersTouched'

export function encodeAdvancedFilters(filters) {
    if (!filters || filters.length === 0) return null
    try {
        return btoa(unescape(encodeURIComponent(JSON.stringify(filters))))
    } catch (e) {
        return null
    }
}

export function decodeAdvancedFilters(param) {
    if (!param) return []
    try {
        const parsed = JSON.parse(decodeURIComponent(escape(atob(param))))
        return Array.isArray(parsed) ? parsed : []
    } catch (e) {
        return []
    }
}

// Detail pages (e.g. Cars/CarDetailNew/CarDetailContainer.js) keep every tab
// they have shown mounted but hidden, and all of those tabs share the one
// `?filter=` param. Without a scope, a hidden tab would follow the URL too and
// re-run its query with filters meant for another tab's entity. Wrapping a tab
// in <AdvancedFiltersScope active={false}> makes useAdvancedFilters hand that
// tab the filters it last saw while it was active, ignoring URL changes.
const AdvancedFiltersScopeContext = createContext(true)

export function AdvancedFiltersScope({ active, children }) {
    return <AdvancedFiltersScopeContext.Provider value={active}>{children}</AdvancedFiltersScopeContext.Provider>
}

// The `?filter=` query param is the single source of truth for advanced
// filters -- any component can call this hook to read the current filters or
// change them, with no prop drilling. Every consumer calling useSearchParams()
// re-renders when the URL changes, regardless of which component wrote it, so
// e.g. the popover applying a filter and a chip removing one both just work.
export function useAdvancedFilters() {
    const [searchParams] = useSearchParams()
    const location = useLocation()
    const navigate = useNavigate()
    const active = useContext(AdvancedFiltersScopeContext)
    const urlFilters = useMemo(() => decodeAdvancedFilters(searchParams.get(FILTER_PARAM)), [searchParams])
    const lastActiveFilters = useRef(urlFilters)
    if (active) lastActiveFilters.current = urlFilters
    const filters = lastActiveFilters.current

    // Deliberately not useSearchParams()'s own setter: it always calls
    // navigate("?" + params, …) - a bare search string with no hash - and
    // react-router's resolveTo() defaults a missing hash to "" (see
    // @remix-run/router's resolvePath), which silently drops any tab a page keeps
    // in the hash. Building the "to" object ourselves and carrying over the current
    // location.hash keeps it intact. Every sidebar listing page (Material/TabbedListingPage.js,
    // including Organization) now keeps its active tab in the path instead (see
    // Functions/pathTabs.js), so this carry-over no longer matters for any of them - but
    // several detail pages (e.g. Drivers/Driver/Detail/DetailsNew/DriverDetailContainerNew.js,
    // Cars/CarDetailNew/CarDetailContainer.js) still track their own sub-tab in
    // window.location.hash, and this hook is used on those pages too, so the carry-over stays.
    const setFilters = useCallback((newFilters) => {
        const nextParams = new URLSearchParams(location.search)
        const encoded = encodeAdvancedFilters(newFilters)
        if (encoded) {
            nextParams.set(FILTER_PARAM, encoded)
        } else {
            nextParams.delete(FILTER_PARAM)
        }
        // Marks this history entry as "the filters here were set by the user".
        // A page that persists filters needs to tell a real edit apart from a
        // plain navigation that happens to drop `?filter=` -- every in-app link
        // is built without a search string, so following one looks identical to
        // clearing every filter by hand. Existing state is preserved: other
        // pages put their own things here.
        navigate(
            { pathname: location.pathname, search: nextParams.toString(), hash: location.hash },
            { replace: true, state: { ...(location.state || {}), [FILTERS_TOUCHED_STATE_KEY]: true } }
        )
    }, [navigate, location])

    return [filters, setFilters]
}

// True only on a history entry whose filters were set through setFilters above.
export function advancedFiltersWereTouched(location) {
    return !!location?.state?.[FILTERS_TOUCHED_STATE_KEY]
}
